import { useEffect, useMemo, useRef, useState } from "react";
import {
	WizardBody,
	WizardFooter,
	WizardHeader,
	WizardStepContent,
} from "../popups/wizard/WizardModal";
import StatusContent from "./StatusContent";
import BoxPlanOrganizationTemplate from "../popups/BoxPlanOrganizationTemplate";
import useNotification from "../../hooks/notification.hook";
import { exportCreate, exportShow, getPlanApprovals } from "../../services/plans.service";
import { getPresetSelection } from "../../helper/formatter.helper";
import useStatusContentHook from "../../hooks/plan-download/use-status-content.hook";
import useTemplatePickerHook from "../../hooks/plan-download/use-template-picker.hook";
import AdditionalPrintSettings from "./AdditionalPrintSettings";
import useAdditionalSettingsPrintHook, {
	INIT_VALUE_APPROVALS_SETTINGS,
	INIT_VALUE_DEADLINES_SETTINGS,
} from "../../hooks/plan-download/use-additional-settings-print.hook";
import { cn } from "../../lib/utils";
import { useSessionStore } from "../../store/session.store";
import { getTemplatePresets } from "../../services/presets.service";

/**
 * @typedef {Object} PlanDownloadContentPickerProps
 *
 * @property {boolean} open
 *
 * @property {Array<Object>} template
 * @property {(number|string)} template[].id
 * @property {string} template[].name
 * @property {Array<Object>} template[].contents
 * @property {(number|string)} template[].contents[].id
 * @property {string} template[].contents[].name
 * @property {"chapter_content"|"chapter_goal"} template[].contents[].content_type
 * @property {boolean} [template[].contents[].editable]
 *
 * @property {Array<Object>} [template[].contents[].requirements]
 * @property {(number|string)} template[].contents[].requirements[].id
 * @property {string} template[].contents[].requirements[].name
 *
 * @property {() => void} onFinish
 * @property {() => void} onStart
 *
 * @property {any[]} [statuses]
 * @property {any[]} [presets]
 *
 * @property {Array<Object>} [selectedOrganizationsPlans]
 * @property {number} selectedOrganizationsPlans[].organization_id
 * @property {number} selectedOrganizationsPlans[].organization_plan_id
 *
 * @property {number} [stepStart]
 *
 * @property {Object} [stepConfiguration]
 * @property {Object} [stepConfiguration.status]
 * @property {string} [stepConfiguration.status.title]
 * @property {string} [stepConfiguration.status.description]
 * @property {string} [stepConfiguration.status.label_cta]
 *
 * @property {Object} [stepConfiguration.template]
 * @property {string} [stepConfiguration.template.title]
 * @property {string} [stepConfiguration.template.description]
 * @property {string} [stepConfiguration.template.label_cta]
 *
 * @property {Object} [stepConfiguration.approvals]
 * @property {string} [stepConfiguration.approvals.description]
 * @property {boolean} [stepConfiguration.approvals.showSelection]
 */
const PlanDownloadContentPicker = ({
	open,
	template,
	onFinish,
	onStart,
	statuses = [],
	selectedOrganizationsPlans = [],
	stepStart = 1,
	stepConfiguration,
	presetOrganizationId,
	templateId,
}) => {
	const { openNotification, helperErrorNotification } = useNotification();

	const [presets, setPresets] = useState([]);

	const [isLoadingPresets, setIsLoadingPresets] = useState(false);

	const { user } = useSessionStore();

	const { toggleAllStatus, toggleStatus, selectedStatus, setSelectedStatus } =
		useStatusContentHook(statuses);

	const { updateDeadlinesSettings, deadlinesSettings, approvalsSettings, updateApprovalsSettings } =
		useAdditionalSettingsPrintHook();

	const { selectedTemplateConfiguration, setSelectedTemplateConfiguration } =
		useTemplatePickerHook();

	const [loadingButton, setLoading] = useState([]);
	const [selectedFormat, setSelectedFormat] = useState("pdf");
	const [selectedPreset, setSelectedPreset] = useState("");
	const [isPreventedApprovals, setIsPreventedApprovals] = useState(false);

	const loading = useRef([]);
	const timerDownload = useRef(null);

	const csvTemplate = useMemo(
		() =>
			template.map((ch) => ({
				...ch,
				contents: ch.contents.filter((c) => c.content_type === "chapter_goal"),
			})),
		[template]
	);

	const getPresetsList = async () => {
		if (!templateId || presets.length > 0 || isLoadingPresets) return;

		setIsLoadingPresets(true);

		await getTemplatePresets(
			{
				id: templateId,
				include_options: 1,
				paginate: 0,
				filter: {
					accessible_by_organization: presetOrganizationId ? [presetOrganizationId] : undefined,
				},
			},
			(res) => {
				setPresets(res.data);
			}
		);

		setIsLoadingPresets(false);
	};

	useEffect(() => {
		if (!open) return;

		getPresetsList();

		setSelectedTemplateConfiguration({
			chapters: [],
			contents: [],
			goals: [],
			reqs: [],
		});
		updateDeadlinesSettings(INIT_VALUE_DEADLINES_SETTINGS);
		updateApprovalsSettings(INIT_VALUE_APPROVALS_SETTINGS);
		setSelectedStatus([]);
		setSelectedFormat("pdf");
		setSelectedPreset("");
		setIsPreventedApprovals(false);
	}, [open]);

	useEffect(() => {
		if (!selectedPreset) return;
		const preset = presets.find((p) => p.id === Number(selectedPreset));
		setSelectedStatus(preset?.options?.organizationRequirementsStatuses || []);

		let data = {
			chapters: [],
			contents: [],
			goals: [],
			reqs: [],
		};

		if (preset) {
			data = getPresetSelection(
				template,
				preset?.options?.templateRequirements,
				preset?.options?.templateChapterContents
			);
		}
		setSelectedTemplateConfiguration(data);
		updateDeadlinesSettings({
			included: !!preset?.options?.deadlines,
			from: preset?.options?.deadline_date_from,
			to: preset?.options?.deadline_date_to,
		});
		updateApprovalsSettings({
			included: !preset?.options?.hide_approval_section,
		});
	}, [selectedPreset]);

	const isCsvEnable = useMemo(() => ["admin", "reseller"].includes(user?.role), [user?.role]);

	/**
	 * Create Download
	 */
	const createDownload = (printOnlyDeadlines) => {
		if (printOnlyDeadlines && !deadlinesSettings.included) return;

		if (loading.current.length === 0 && selectedOrganizationsPlans.length > 0) {
			onStart?.();
			setLoading([...Array(selectedOrganizationsPlans.length)]);
			loading.current = [...Array(selectedOrganizationsPlans.length)];

			let templateChapters;
			let templateChapterContents;
			let templateGoals;
			let templateRequirements;

			if (!printOnlyDeadlines) {
				const { chapters, goals, contents, reqs } = selectedTemplateConfiguration;

				templateChapters = chapters;
				templateChapterContents = contents.map((c) => c.id);
				templateGoals = goals.map((g) => g.id);
				templateRequirements = reqs.map((r) => r.id);
			}

			selectedOrganizationsPlans.forEach(({ organization_id, organization_plan_id }) => {
				const body = {
					idCorp: organization_id,
					idPlan: organization_plan_id,
					options: {
						organizationRequirementsStatuses: selectedStatus,
						templateChapters,
						templateChapterContents,
						templateGoals,
						templateRequirements,
						...((deadlinesSettings.included || selectedFormat === "csv") && {
							deadlines: deadlinesSettings.included,
							deadline_date_from: deadlinesSettings.from || undefined,
							deadline_date_to: deadlinesSettings.to || undefined,
						}),
						hide_approval_section: !approvalsSettings.included,
						...(approvalsSettings.included && {
							show_only_last_approval: !approvalsSettings?.selected.length,
							organization_plan_approvals: approvalsSettings?.selected,
						}),
					},
					format: selectedFormat,
				};

				exportCreate(
					body,
					(res) => {
						getDownload(res.data.id, organization_id, organization_plan_id);
					},
					(error) => {
						helperErrorNotification(error);
						loading.current.pop();
						setLoading((l) => l.slice(0, -1));
					}
				);
			});
		}
	};

	/**
	 * On change format
	 * @param {*} format
	 */
	const onChangeFormat = (format) => {
		if (format === "csv") {
			setSelectedTemplateConfiguration((selected) => ({
				...selected,
				contents: [],
			}));
			/*updateDeadlinesSettings(INIT_VALUE_DEADLINES_SETTINGS);*/
			updateApprovalsSettings({ included: false });
		}

		setSelectedFormat(format);
	};

	/**
	 * Get Download
	 */
	const getDownload = (id, idCorp, idPlan) => {
		exportShow(
			{ idCorp, idPlan, id },
			(res) => {
				if (res.data.status === "pending") {
					timerDownload.current = setTimeout(() => {
						getDownload(id, idCorp, idPlan);
					}, 3000);
				} else {
					if (!res.data.url) {
						helperErrorNotification();
					} else {
						const link = document.body.appendChild(document.createElement("a"));
						link.href = res.data.url;
						link.target = "_blank";
						link.download = " ";
						link.click();
						link.href = "";
						document.body.removeChild(link);

						openNotification({
							text:
								selectedOrganizationsPlans.length > 1
									? "Documenti generati con successo!"
									: "Documento generato con successo!",
							status: "success",
						});
					}
					loading.current.pop();
					setLoading((l) => l.slice(0, -1));
					if (loading.current.length === 0) onFinish?.();
				}
			},
			(error) => {
				helperErrorNotification(error);
				loading.current.pop();
				setLoading((l) => l.slice(0, -1));
			}
		);
	};

	const getApprovals = async () => {
		const organizationPlan = selectedOrganizationsPlans?.[0];

		if (!organizationPlan || isPreventedApprovals) return;

		updateApprovalsSettings({ isLoading: true });
		await getPlanApprovals(
			{
				idCorp: organizationPlan.organization_id,
				idPlan: organizationPlan.organization_plan_id,
			},
			(res) => {
				updateApprovalsSettings({ list: res.data });
			}
		);
		updateApprovalsSettings({ isLoading: false });
		setIsPreventedApprovals(true);
	};

	return (
		<>
			{/* Status */}
			<WizardStepContent stepIndex={stepStart}>
				<WizardHeader
					title={stepConfiguration?.status?.title || "Genera documento"}
					showBackButton={stepStart > 1}
					description={
						stepConfiguration?.status?.description ||
						"Seleziona gli stati delle linee d’azione da includere"
					}
					rightContent={
						<div className="main_select_landing">
							<select
								defaultValue={selectedFormat}
								onChange={(e) => onChangeFormat(e.target.value)}
								className="landing big"
							>
								<option value="pdf">Formato PDF</option>
								<option value="docx">Formato DOCX</option>
								{isCsvEnable && <option value="csv">Formato CSV</option>}
							</select>
							<div className="wrap_arrow">
								<span className={"icon_16 default chevron"}></span>
							</div>
						</div>
					}
				/>

				<WizardBody stepIndex={stepStart}>
					<div className="px-7 pt-5 pb-6 border-b border-dark-10 global_form_landing">
						<div className="base">
							<div className="label_input">Inizia da una configurazione</div>
							<div className="main_select_landing w-full max-w-full">
								<div className="relative">
									<select
										disabled={isLoadingPresets}
										className="landing big"
										value={selectedPreset}
										onChange={(e) => setSelectedPreset(e.target.value)}
									>
										<option disabled value="">
											Seleziona configurazione
										</option>
										{!!presets?.filter((preset) => preset.visibility === "public")?.length && (
											<optgroup label="Configurazioni standard">
												{presets
													.filter((preset) => preset.visibility === "public")
													.map((preset, i) => (
														<option key={i} value={preset.id}>
															{preset.name}
														</option>
													))}
											</optgroup>
										)}
										{!!presets?.filter((preset) => preset.visibility === "private")?.length && (
											<optgroup label="Configurazioni ente">
												{presets
													?.filter((preset) => preset.visibility === "private")
													.map((preset, i) => (
														<option key={i} value={preset.id}>
															{preset.name}
														</option>
													))}
											</optgroup>
										)}
									</select>
									<div className="wrap_arrow">
										<span
											className={cn(
												"icon_16 default",
												isLoadingPresets ? "loader" : "chevron icon_mask !bg-interactive-100"
											)}
										></span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<StatusContent
						selectedStatues={selectedStatus}
						listStatuses={statuses}
						onToggleStatus={toggleStatus}
						onToggleAllStatus={toggleAllStatus}
					/>
				</WizardBody>

				<WizardFooter nextButtonText={stepConfiguration?.status?.label_cta || "Avanti"} />
			</WizardStepContent>

			{/* Deadlines and approvals */}

			<WizardStepContent stepIndex={stepStart + 1}>
				<WizardHeader
					title={stepConfiguration?.status?.title || "Genera documento"}
					showBackButton
					description={
						stepConfiguration?.status?.description ||
						"Seleziona le impostazioni dei cotenuti in stampa"
					}
				/>

				<WizardBody
					stepIndex={stepStart + 1}
					transitionProps={{
						onEnter: () => !!stepConfiguration?.approvals?.showSelection && getApprovals(),
					}}
				>
					<AdditionalPrintSettings
						csvMode={selectedFormat === "csv"}
						deadlinesSettings={deadlinesSettings}
						updateDeadlinesSettings={updateDeadlinesSettings}
						approvalsSettings={approvalsSettings}
						updateApprovalSettings={updateApprovalsSettings}
						approvalDescription={stepConfiguration?.approvals?.description}
						showApprovalsSelection={stepConfiguration?.approvals?.showSelection}
					/>
				</WizardBody>

				<WizardFooter
					leftContent={
						selectedFormat !== "csv" && (
							<button
								type="button"
								disabled={!deadlinesSettings.included}
								className={cn(
									"button line whitespace-nowrap",
									!deadlinesSettings.included && "disabled",
									loadingButton.length > 0 && "loading"
								)}
								onClick={() => createDownload(true)}
							>
								<div className="load_content">Stampa scadenzario ed esci</div>
								{loadingButton.length > 0 && (
									<div className="main_loader_icon !bg-dark-70 fade_in"></div>
								)}
							</button>
						)
					}
					nextButtonText={stepConfiguration?.status?.label_cta || "Avanti"}
					disableNext={loadingButton.length > 0}
				/>
			</WizardStepContent>

			{/* Step template */}
			<WizardStepContent stepIndex={stepStart + 2}>
				<WizardHeader
					title={stepConfiguration?.template?.title || "Genera documento"}
					description={
						stepConfiguration?.template?.description ||
						"Seleziona il contenuto ed il formato del file"
					}
					showBackButton
				/>

				<WizardBody stepIndex={stepStart + 2}>
					<div className="py-2">
						<BoxPlanOrganizationTemplate
							template={selectedFormat === "csv" ? csvTemplate : template}
							setSelectedItems={setSelectedTemplateConfiguration}
							selectedItems={selectedTemplateConfiguration}
						/>
					</div>
				</WizardBody>

				<WizardFooter
					nextButtonText={stepConfiguration?.template?.label_cta || "Genera Documento"}
					onNext={() => createDownload()}
					isLoading={loadingButton.length > 0}
				/>
			</WizardStepContent>
		</>
	);
};

export default PlanDownloadContentPicker;
